/* How much viewport bottom the bar is covering, read by the floating widgets'
   CSS. Set on the root rather than passed around so it survives the bar being
   torn down and rebuilt by a swatch swap. */
function setBottomOffset(px) {
	document.documentElement.style.setProperty('--sticky-atc-height', `${px}px`);
}

/**
 * <sticky-add-to-cart>
 *
 * Floating PDP add-to-cart bar. Shows when the main PDP add-to-cart button is
 * scrolled off screen, adds to cart via its own variant-selector, and keeps its
 * size selection in sync (both ways) with the main PDP variant selector.
 */
class StickyAddToCart extends HTMLElement {
	constructor() {
		super();
		// Guards against sync feedback loops when we programmatically click radios.
		this._syncing = false;
		// Set in initVisibility(); see measureHeaderOffset().
		this.headerOffset = 0;
	}

	connectedCallback() {
		this.stickySelector = this.querySelector('variant-selector');
		// Direct child: the Buy More & Save rows carry selectors inside the form too.
		this.pdpSelector = document.querySelector('.product-form > variant-selector');
		this.atcButton = document.getElementById('AddToCart-product-template');
		this.button = this.querySelector('.sticky-atc__button');
		this.priceEl = this.querySelector('.sticky-atc__price');

		// Nothing to attach to (eg register-interest / no ATC on this product).
		if (!this.stickySelector || !this.pdpSelector || !this.atcButton) {
			// A swap may be leaving a previous product's offset behind.
			setBottomOffset(0);
			this.remove();
			return;
		}

		// Before initVisibility(), which seeds the visible state and needs to be
		// able to publish the resulting offset.
		this.initBottomOffset();
		this.initVisibility();
		this.initSync();
		this.initAddToCart();
		this.initSwatchDropdown();
		this.initReviewsFit();
		this.initSelectorRebind();
	}

	disconnectedCallback() {
		document.removeEventListener('variantselectorreplaced', this.onSelectorReplaced);
		setBottomOffset(0);
	}

	/* Publish how much of the viewport bottom the bar is covering, so the
	 * floating Google review badge and chat button can ride up clear of it.
	 *
	 * Only the mobile layout is bottom-docked — on desktop the bar pins under
	 * the site header and covers nothing at the bottom, so the offset stays 0
	 * and the widgets keep their resting position (WEB-3179).
	 */
	initBottomOffset() {
		const bottomDocked = window.matchMedia('(max-width: 768px)');

		this.publishBottomOffset = () => {
			// A swap leaves the previous bar's observers alive for a beat; without
			// this they'd measure a detached element and zero out the new bar's
			// offset on the next resize or breakpoint change.
			if (!this.isConnected) return;

			const visible = this.classList.contains('sticky-atc--visible');
			setBottomOffset(bottomDocked.matches && visible ? this.offsetHeight : 0);
		};

		this.publishBottomOffset();
		// The bar's height moves with its content (a two-line title, a discount
		// on the price) as well as with the viewport.
		new ResizeObserver(() => this.publishBottomOffset()).observe(this);
		bottomDocked.addEventListener('change', () => this.publishBottomOffset());
	}

	/* Drop the review rating when the bar's two halves can't both fit.
	 *
	 * The actions don't shrink and the title and price don't wrap, so on narrower
	 * desktops the info group overflows instead — around 1024px, though where
	 * exactly depends on how long the product name is and whether the price is
	 * carrying a discount. The rating is the supplementary part, so it gives way
	 * (WEB-3156).
	 *
	 * Measured rather than pinned to a breakpoint: the widths above swing a
	 * couple of hundred pixels between products, so any single breakpoint either
	 * still overflows the long ones or strips the rating off short ones that fit.
	 */
	initReviewsFit() {
		this.info = this.querySelector('.sticky-atc__info');
		if (!this.info || !this.querySelector('.sticky-atc__reviews')) return;

		// The bar is a different layout below this, with the rating already out.
		const desktop = window.matchMedia('(min-width: 769px)');

		this.fitReviews = () => {
			if (!desktop.matches) return;

			// Put the rating back before measuring — hidden it contributes no
			// width, so there'd be nothing to tell us whether it would fit. Both
			// writes land in one task, so the restored state is never painted.
			this.classList.remove('sticky-atc--no-reviews');
			if (this.info.scrollWidth > this.info.clientWidth) {
				this.classList.add('sticky-atc--no-reviews');
			}
		};

		this.fitReviews();
		new ResizeObserver(() => this.fitReviews()).observe(this);
	}

	/* Show/hide the bar based on the main ATC button's visibility. */
	initVisibility() {
		const target = this.atcButton.closest('.product-form__item--submit') || this.atcButton;

		this.headerOffset = this.measureHeaderOffset();

		// Suppress the slide-in transition until the initial visibility state is
		// applied. Otherwise, when the bar is re-created during a swatch/section
		// swap while already scrolled past the ATC button, it would animate in
		// from scratch instead of just being there.
		this.classList.add('sticky-atc--no-transition');

		// Seed the visibility state synchronously from the target's position.
		// The IntersectionObserver's first callback is async (a frame or two
		// later), so relying on it alone leaves the re-created bar flashing
		// hidden before it pops back in.
		this.classList.toggle('sticky-atc--visible', this.shouldShow(target.getBoundingClientRect()));
		this.publishBottomOffset();

		let initialised = false;

		const observer = new IntersectionObserver(
			entries => {
				for (const entry of entries) {
					this.classList.toggle('sticky-atc--visible', this.shouldShow(entry.boundingClientRect));
				}

				this.publishBottomOffset();

				if (!initialised) {
					initialised = true;
					requestAnimationFrame(() => this.classList.remove('sticky-atc--no-transition'));
				}
			},
			// Inset the root's top edge by the header, so the callback fires at the
			// same line shouldShow() measures against.
			{ threshold: 0, rootMargin: `-${this.headerOffset}px 0px 0px 0px` }
		);

		observer.observe(target);
	}

	/* Height of the sticky site header, which overlays the top of the viewport.
	 *
	 * The ATC button disappears behind the header a header's worth of scrolling
	 * before it reaches y=0, leaving a stretch of page with no add-to-cart in
	 * sight. Triggering off the header's bottom edge instead hands over exactly
	 * when the button is covered.
	 *
	 * Read once per setup, not per callback: the observer's rootMargin is fixed
	 * at construction, so a live reading could disagree with it and the bar
	 * would sit out a crossing entirely. Being a few px stale (desktop collapses
	 * the header from 128px to 120px on scroll) just shifts the handover by that
	 * much; the bar re-measures whenever a section swap re-creates it.
	 */
	measureHeaderOffset() {
		const height = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height'));

		return Number.isFinite(height) ? height : 0;
	}

	/* Whether the bar should be showing given the ATC target's viewport rect.
	 *
	 * Only once the ATC button has scrolled up behind the header — never while
	 * it's still below, waiting to be scrolled to. That keeps the bar off the
	 * page on load at both breakpoints (WEB-3149); mobile previously showed it
	 * immediately, because the ATC starts below the fold.
	 */
	shouldShow(rect) {
		return rect.bottom <= this.headerOffset;
	}

	/* Re-bind to the PDP selector if a colour swatch swap replaced it (the new
	 * element announces itself with variantselectorreplaced, carrying the old
	 * one). Our own copy only knows the old product's variants, so adopt the
	 * new markup too, then restore this bar's display from it. */
	initSelectorRebind() {
		this.onSelectorReplaced = e => {
			if (!this.pdpSelector || e.detail?.oldSelector !== this.pdpSelector) return;
			this.pdpSelector = e.target;
			this.adoptSwappedSelector(e.target);
			this.syncFromPdp();
		};
		document.addEventListener('variantselectorreplaced', this.onSelectorReplaced);
	}

	/* Adopt a copy of the colour-swapped PDP selector, minus the colour
	 * dropdown and price row the bar doesn't show, with ids and radio names
	 * suffixed so they can't collide with the PDP's (the uid suffix's job on
	 * the original render). */
	adoptSwappedSelector(selector) {
		const copy = selector.cloneNode(true);
		copy.querySelector('colour-swatch-selector')?.remove();
		copy.querySelector('.variant-selector__price-wrapper')?.remove();
		copy.removeAttribute('data-preorder-popup');
		copy.removeAttribute('lazyload');
		copy.querySelectorAll('[id]').forEach(el => el.id = `${el.id}-sticky`);
		copy.querySelectorAll('label[for]').forEach(el => el.setAttribute('for', `${el.getAttribute('for')}-sticky`));
		copy.querySelectorAll('option-selector input[type="radio"]').forEach(el => el.name = `${el.name}-sticky`);
		this.stickySelector.replaceWith(copy);
		this.stickySelector = copy;
		// The sync listeners died with the elements they were bound to.
		this.bindStickySync(copy);
		this.bindPdpSync(selector);
	}

	/* Keep the sticky and PDP selectors in sync. */
	initSync() {
		this.bindStickySync();
		this.bindPdpSync();
	}

	/* Bound per selector rather than once: a colour swap replaces both this
	 * bar's copy and the PDP's wholesale, and the replacements need their own
	 * listeners (see adoptSwappedSelector). */
	bindStickySync(selector = this.stickySelector) {
		selector.addEventListener('variantchange', e => {
			this.updatePrice(e.detail.variant);
			this.updateButton(e.detail.variant);
			// Only propagate genuine user changes; programmatic clicks are untrusted.
			if (e.detail.trusted) this.syncOptions(selector, this.pdpSelector);
		});
	}

	bindPdpSync(selector = this.pdpSelector) {
		selector.addEventListener('variantchange', e => {
			if (!e.detail.trusted) return;
			// Mirror into the sticky selector; its own variantchange updates our display.
			this.syncOptions(selector, this.stickySelector);
		});
	}

	/* Adopt whatever the PDP selector currently has selected.
	 *
	 * A swatch swap re-renders the section — this bar included — from the new
	 * product's defaults, then restores the shopper's size into the main form
	 * with synthetic change events that initSync() deliberately ignores as
	 * untrusted. Nothing else told the bar about that restore, so it sat on the
	 * default size while the page showed the chosen one (WEB-3177).
	 */
	syncFromPdp() {
		if (!this.pdpSelector || !this.stickySelector) return;
		this.syncOptions(this.pdpSelector, this.stickySelector);
	}

	/* Copy each selected option value from one selector into the other. */
	syncOptions(from, to) {
		if (this._syncing) return;
		this._syncing = true;

		for (const fromOption of from.querySelectorAll('option-selector')) {
			const key = fromOption.dataset.option;
			const value = fromOption.dataset.selected;
			if (!value) continue;

			const toOption = to.querySelector(`option-selector[data-option="${key}"]`);
			if (!toOption) continue;

			const input = [...toOption.querySelectorAll('input[type="radio"]')].find(radio => radio.value === value);
			if (input && !input.checked) input.click();
		}

		this._syncing = false;
	}

	updatePrice(variant) {
		if (this.priceEl && variant.priceText) this.priceEl.innerHTML = variant.priceText;
		// A discount appearing or dropping off changes how wide the price runs,
		// and with it whether the rating still fits beside it.
		this.fitReviews?.();
	}

	updateButton(variant) {
		const label = this.button.querySelector('.sticky-atc__button-label');
		if (label && variant.addToCartLabel) label.innerHTML = variant.addToCartLabel;
		this.button.disabled = !variant.addToCartEnabled;
	}

	initAddToCart() {
		this.button.addEventListener('click', async () => {
			if (this.button.disabled) return;

			this.button.classList.add('show-loading', 'show-loading--inverted');

			/* The main button's own add, which places the whole basket — this
			   product plus every piece ticked in Buy More & Save — so the bar
			   buys what the page is offering rather than this product alone. The
			   two selectors are kept in sync both ways, so it adds the variant
			   showing here. Resolved at click time: product-page.js publishes it
			   on DOM ready, which may be after this bar connects. */
			if (this.atcButton.addToCart) await this.atcButton.addToCart();
			else await this.stickySelector.addToCart();

			this.button.classList.remove('show-loading', 'show-loading--inverted');
		});
	}

	/* Wire the colour swatch trigger to the slide-out-modal dropdown. */
	initSwatchDropdown() {
		const trigger = this.querySelector('.sticky-atc__swatch-trigger');
		const modal = this.querySelector('.sticky-atc__swatch-modal');
		if (!trigger || !modal) return;

		trigger.addEventListener('click', () => modal.toggle());
		modal.addEventListener('modalopen', () => trigger.setAttribute('aria-expanded', 'true'));
		// Flip the arrow the moment closing starts, not after the modal's 300ms
		// close animation finishes (modalclose), so it tracks the dropdown like
		// the variant selector's arrow does.
		modal.addEventListener('modalstartclose', () => trigger.setAttribute('aria-expanded', 'false'));
	}
}

customElements.define('sticky-add-to-cart', StickyAddToCart);
